import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Plus, AlertTriangle } from 'lucide-react';
import { formsApi, getErrorMessage } from '../../services/api';

const TEMPLATE_PROMPTS = [
  {
    label: 'Right to Work (UK)',
    prompt: 'A UK Right to Work form for a candidate. Collect full name, date of birth, nationality, passport number with upload, share code (if non-British), employment start date, and signature.',
  },
  {
    label: 'Reference check',
    prompt: 'A professional reference questionnaire for a previous manager. Ask about working dates, role, key responsibilities, strengths, areas for development, would-rehire (yes/no), and overall rating out of 5.',
  },
  {
    label: 'New starter onboarding',
    prompt: 'New starter onboarding form for a permanent UK candidate. Personal details, emergency contact, bank details, NI number, P45 upload, tax declaration radio buttons, and signed acceptance of company handbook.',
  },
  {
    label: 'Compliance documents (healthcare)',
    prompt: 'Compliance check for an NHS healthcare worker — DBS number with issue date, NMC/HCPC PIN, mandatory training expiry dates, occupational health clearance, two references (name/email/relationship), and signed declaration.',
  },
];

/**
 * AI-powered form generator dialog. Recruiter describes what they need
 * in plain English, the backend calls Claude Sonnet via the Emergent
 * LLM key, and returns a preview of fields. The recruiter chooses to
 * insert them (append to the current form) or discard.
 */
export const AiGenerateDialog = ({ open, onClose, entityType, existingFieldCount, onInsert }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Describe what you want the form to collect');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await formsApi.generateWithAi({
        prompt: prompt.trim(),
        entity_type: entityType || 'candidate',
        existing_field_count: existingFieldCount || 0,
      });
      setGenerated(data.fields || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const friendly =
        err.response?.status === 503
          ? (typeof detail === 'string' ? detail : 'AI is temporarily unavailable.')
          : getErrorMessage(err, 'AI generation failed');
      setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (!generated?.length) return;
    onInsert(generated);
  };

  const handleClose = () => {
    setPrompt('');
    setGenerated(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            AI Form Generator
          </DialogTitle>
          <DialogDescription>
            Describe what you need in plain English. Claude will design the fields and you choose what to keep.
          </DialogDescription>
        </DialogHeader>

        {generated ? (
          <>
            <div className="space-y-2 py-2" data-testid="ai-generate-preview">
              <div className="text-sm font-medium text-foreground">
                Generated {generated.length} field{generated.length === 1 ? '' : 's'} — will append to your form
              </div>
              <div className="border border-border rounded-lg divide-y divide-border bg-slate-50/50">
                {generated.map((f, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-2" data-testid={`ai-generate-preview-row-${i}`}>
                    <span className="text-[10px] uppercase font-semibold tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
                      {f.field_type}
                    </span>
                    <span className="text-sm flex-1 truncate">{f.label}</span>
                    {f.required && (
                      <span className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider">Required</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGenerated(null)} className="gap-1.5" data-testid="ai-generate-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button onClick={handleInsert} className="gap-1.5" data-testid="ai-generate-insert-btn">
                <Plus className="w-4 h-4" /> Insert all
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">Describe your form</Label>
                <Textarea
                  rows={5}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g. A short Right to Work check for a UK candidate — collect passport details, share code, and an e-signature."
                  data-testid="ai-generate-prompt-input"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Quick starts</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {TEMPLATE_PROMPTS.map((t) => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => setPrompt(t.prompt)}
                      className="text-left px-3 py-2 rounded-lg border border-border hover:border-violet-300 hover:bg-violet-50 transition-colors"
                      data-testid={`ai-generate-template-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <p className="text-sm font-medium text-foreground">{t.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2" data-testid="ai-generate-error">
                  <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-rose-800">{error}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="ai-generate-cancel-btn">Cancel</Button>
              <Button onClick={handleGenerate} disabled={loading || !prompt.trim()} className="gap-1.5" data-testid="ai-generate-submit-btn">
                <Sparkles className="w-4 h-4" />
                {loading ? 'Generating…' : 'Generate fields'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
